import { TextField as MUITextField, Typography } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TableRow from "@material-ui/core/TableRow";
import { DatePicker } from "@material-ui/pickers";
import {
  DistrictField,
  ProvinceField,
  SubDistrictField,
  useAddress,
  ZipcodeField
} from "components/ltr-request-document/components/Address";
import {
  StyleCheckbox,
  StyledTableCell,
  useStyles
} from "components/request-document/components/Commons";
import { getErrorMessage, isError } from "libs/utils/form";
import { get } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { canEditForm } from "redux/modules/authSlice";
import { selectMasterData } from "redux/modules/masterSlice";

const TextField = React.memo(MUITextField);

export default function PersonalInfoSection({ formik, disabled = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const canEdit = useSelector(canEditForm);
  const addressFieldOptions = useAddress({
    formik,
    ProvinceFieldName: "document.PersonalProvince",
    DistrictFieldName: "document.PersonalDistrict",
    SubDistrictFieldName: "document.PersonalSubDistrict",
    ZipcodeFieldName: "document.PersonalZipcode",
  });

  const masterData = useSelector(selectMasterData);
  const nationalityConfig = get(masterData, "nationalityConfig", []);
  const titleConfig = get(masterData, "titleConfig", []);
  const accommodationConfig = get(masterData, "accommodationConfig", []);

  return (
    <>
      <TableRow>
        <StyledTableCell colSpan={7}>
          <div className={classes.cellContent}>
            <div>
              <Typography>Full English name as in passport</Typography>
            </div>
          </div>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <StyledTableCell colSpan={1}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Title</InputLabel>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                // style={{ width: 200 }}
                error={isError("personal.title", formik)}
              >
                <InputLabel>Select Title</InputLabel>
                <Select
                  label="Selected Title"
                  name="personal.titleID"
                  value={formik.values.personal.titleID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled || !canEdit}
                >
                  {titleConfig.map((title) => (
                    <MenuItem value={title.titleID} key={title.titleID}>
                      {title.titleEN}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={isError("personal.titleID", formik)}>
                  {getErrorMessage("personal.titleID", formik)}
                </FormHelperText>
              </FormControl>
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>First name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.fistName"
                value={formik.values.personal.fistName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.fistName", formik)}
                helperText={getErrorMessage("personal.fistName", formik)}
                disabled={disabled || !canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Middle name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.middleName"
                value={formik.values.personal.middleName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.middleName", formik)}
                helperText={getErrorMessage("personal.middleName", formik)}
                disabled={disabled || !canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Last name</InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.lastName"
                value={formik.values.personal.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.lastName", formik)}
                helperText={getErrorMessage("personal.lastName", formik)}
                disabled={disabled || !canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>
                Current Nationality / Nationality at birth, if different
              </InputLabel>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                // style={{ width: 200 }}
                error={isError("personal.nationalityID", formik)}
              >
                <InputLabel>Select your nationality</InputLabel>
                <Select
                  label="Selected your nationality"
                  name="personal.nationalityID"
                  value={formik.values.personal.nationalityID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled || !canEdit}
                >
                  {nationalityConfig.map((nationality) => (
                    <MenuItem
                      value={nationality.nationalityID}
                      key={nationality.nationalityID}
                    >
                      {nationality.nationalityName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={isError("personal.nationalityID", formik)}
                >
                  {getErrorMessage("personal.nationalityID", formik)}
                </FormHelperText>
              </FormControl>
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Date of birth (dd-mm-yyyy)</InputLabel>
              <DatePicker
                // style={{ width: 200 }}
                className={classes.formControl}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Date of birth"
                views={["year", "month", "date"]}
                name={`personal.birthDate`}
                value={get(formik.values, `personal.birthDate`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.birthDate`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.birthDate`, formik)}
                helperText={getErrorMessage(`personal.birthDate`, formik)}
                disabled={disabled || !canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel style={{ wordBreak: "break-all" }}>
                Place of birth (city, province/state, country)
              </InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="personal.placeOfBirth"
                value={formik.values.personal.placeOfBirth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.placeOfBirth", formik)}
                helperText={getErrorMessage("personal.placeOfBirth", formik)}
                disabled={disabled || !canEdit}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Gender</InputLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="personal.gender"
                      value="M"
                      checked={formik.values.personal.gender === "M"}
                      onClick={() => {
                        formik.setFieldValue("personal.gender", "M");
                      }}
                    />
                  }
                  label="Male"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="personal.gender"
                      value="F"
                      checked={formik.values.personal.gender === "F"}
                      onClick={() => {
                        formik.setFieldValue("personal.gender", "F");
                      }}
                    />
                  }
                  label="Female"
                />
              </FormGroup>
              <FormHelperText error={isError("personal.gender", formik)}>
                {getErrorMessage("personal.gender", formik)}
              </FormHelperText>
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={4}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Marital status</InputLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="Single"
                      value="S"
                      checked={formik.values.personal.MaritalID === "S"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "S");
                        formik.setFieldValue("personal.MaritalID", "S");
                      }}
                    />
                  }
                  label="Single"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="Married"
                      value="M"
                      checked={formik.values.personal.MaritalID === "M"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "M");
                        formik.setFieldValue("personal.MaritalID", "M");
                      }}
                    />
                  }
                  label="Married"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="Divorced"
                      value="D"
                      checked={formik.values.personal.MaritalID === "D"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "D");
                        formik.setFieldValue("personal.MaritalID", "D");
                      }}
                    />
                  }
                  label="Divorced"
                />
                <FormControlLabel
                  control={
                    <StyleCheckbox
                      disabled={disabled || !canEdit}
                      name="Window"
                      value="W"
                      checked={formik.values.personal.MaritalID === "W"}
                      onClick={() => {
                        formik.setFieldValue("document.Marittal", "W");
                        formik.setFieldValue("personal.MaritalID", "W");
                      }}
                    />
                  }
                  label="Widowed"
                />
              </FormGroup>
              <FormHelperText error={isError("document.Marittal", formik)}>
                {getErrorMessage("document.Marittal", formik)}
              </FormHelperText>
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Passport number</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.passportNumber"
                value={formik.values.personal.passportNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.passportNumber", formik)}
                helperText={getErrorMessage("personal.passportNumber", formik)}
              />
            </div>
          </div>
        </StyledTableCell>

        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Issued at</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.issuedAt"
                value={formik.values.personal.issuedAt}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.issuedAt", formik)}
                helperText={getErrorMessage("personal.issuedAt", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Date of issue (dd-mm-yyyy)</InputLabel>
              <DatePicker
                disabled={disabled || !canEdit}
                // style={{ width: 200 }}
                className={classes.formControl}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Date of issue"
                views={["year", "month", "date"]}
                name={`personal.dateofIssue`}
                value={get(formik.values, `personal.dateofIssue`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.dateofIssue`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.dateofIssue`, formik)}
                helperText={getErrorMessage(`personal.dateofIssue`, formik)}
              />
            </div>
          </div>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Valid until ( dd-mm-yyyy )</InputLabel>
              <DatePicker
                disabled={disabled || !canEdit}
                className={classes.formControl}
                // style={{ width: 200 }}
                inputVariant="outlined"
                disableFuture={false}
                openTo="year"
                format="DD-MM-YYYY"
                label="Valid until"
                views={["year", "month", "date"]}
                name={`personal.validuntil`}
                value={get(formik.values, `personal.validuntil`, null) || null}
                onChange={(date) => {
                  formik.setFieldValue(
                    `personal.validuntil`,
                    date.format("YYYY-MM-DD")
                  );
                }}
                error={isError(`personal.validuntil`, formik)}
                helperText={getErrorMessage(`personal.validuntil`, formik)}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Address in home country</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="document.PersonalAddressInHome"
                value={formik.values.document?.PersonalAddressInHome}
                onChange={(e) => {
                  formik.setFieldValue(
                    "document.PersonalAddressInHome",
                    e.target.value
                  );
                  formik.setFieldValue(
                    "personal.addressInHomeCountry",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={isError("document.PersonalAddressInHome", formik)}
                helperText={getErrorMessage(
                  "document.PersonalAddressInHome",
                  formik
                )}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>
                Current address in Thailand (if applicable)
              </InputLabel>
              <TextField
                variant="outlined"
                className={classes.textField}
                name="document.PersonalAddressInThai"
                value={formik.values.document?.PersonalAddressInThai}
                disabled={disabled || !canEdit}
                onChange={(e) => {
                  formik.setFieldValue(
                    "document.PersonalAddressInThai",
                    e.target.value
                  );
                  formik.setFieldValue(
                    "personal.addressInThailand",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={isError("document.PersonalAddressInThai", formik)}
                helperText={getErrorMessage(
                  "document.PersonalAddressInThai",
                  formik
                )}
              />
            </div>
          </div>
        </StyledTableCell>

        <StyledTableCell colSpan={1}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Residence Type</InputLabel>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                // style={{ width: 200 }}
                error={isError("document.PersonalResidenceType", formik)}
              >
                <InputLabel>Residence Type</InputLabel>
                <Select
                  label="Residence  Type"
                  name="document.PersonalResidenceType"
                  value={formik.values.document?.PersonalResidenceType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled || !canEdit}
                >
                  {accommodationConfig.map((title) => (
                    <MenuItem
                      value={title.accommodationID}
                      key={title.accommodationID}
                    >
                      {title.accommodationName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={isError("document.PersonalResidenceType", formik)}
                >
                  {getErrorMessage("document.PersonalResidenceType", formik)}
                </FormHelperText>
              </FormControl>
            </div>
          </div>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Soi</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.soi"
                inputProps={{ maxLength: 10 }}
                value={formik.values.personal.soi}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.soi", formik)}
                helperText={getErrorMessage("personal.soi", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={4}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Road</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.road"
                value={formik.values.personal.road}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.road", formik)}
                helperText={getErrorMessage("personal.road", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <StyledTableCell colSpan={2}>
          <ProvinceField {...addressFieldOptions} disabled={disabled} />
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <DistrictField {...addressFieldOptions} disabled={disabled} />
        </StyledTableCell>
        <StyledTableCell colSpan={2}>
          <SubDistrictField {...addressFieldOptions} disabled={disabled} />
        </StyledTableCell>
        <StyledTableCell colSpan={1}>
          <ZipcodeField {...addressFieldOptions} disabled={disabled} />
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <StyledTableCell colSpan={3}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>Home / mobile phone number</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.mobile"
                onKeyDown={(e) => {
                  const pattern = /[^0-9^+]/g;
                  const isNotAllow = e.key.length === 1 && pattern.test(e.key);

                  if (isNotAllow) {
                    e.preventDefault();
                  }
                }}
                value={formik.values.personal.mobile}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.mobile", formik)}
                helperText={getErrorMessage("personal.mobile", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
        <StyledTableCell colSpan={4}>
          <div className={classes.cellContent}>
            <div className={classes.wrapField}>
              <InputLabel>E-mail address</InputLabel>
              <TextField
                disabled={disabled || !canEdit}
                variant="outlined"
                className={classes.textField}
                name="personal.email"
                value={formik.values.personal.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={isError("personal.email", formik)}
                helperText={getErrorMessage("personal.email", formik)}
              />
            </div>
          </div>
        </StyledTableCell>
      </TableRow>
    </>
  );
}
